import { defineStore } from 'pinia';
import { doc, getDoc, where } from 'firebase/firestore';
import type { Client, Quote, QuoteLineItem, QuotePaymentPlan } from '~/types/models';
import { DEFAULT_CURRENCY } from '~/constants/currencies';
import { resolveDocumentRecipient } from '~/lib/documentRecipients';
import { dispatchNotification } from '~/lib/notificationDispatch';
import { buildDocumentCreatorFields } from '~/lib/documentCreator';
import { stripUndefined } from '~/lib/stripUndefined';
import { amountFromInstallment, buildDocumentAccounting, installmentLabel } from '~/lib/documentAccounting';
import { buildClientPortalLink, buildClientPortalPath, buildPublicQuoteLink, resolveClientEmailOrigin, resolveWorkspaceOrigin } from '~/lib/tenantLinks';
import { generatePublicAccessToken } from '~/lib/publicAccessTokens';
import { toMillis } from '~/lib/timestamps';
import { runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';
import { resolveWorkspaceEntitlements } from '~/lib/subscriptionPlans';
import { getRequiredAuthHeaders } from '~/lib/apiAuth';
import { resolveQuoteAcceptanceSource } from '~/lib/quoteLifecycle';
import { assignCommercialDocumentNumber, reconcileMissingCommercialDocumentNumbers, requestNextCommercialDocumentNumber } from '~/lib/commercialDocumentNumbering';
import {
    createTenantDocument,
    fetchTenantCollection,
    updateTenantDocument,
    type TenantDocumentPayload,
} from '~/lib/tenantCollection';

type CreateQuoteInput = {
    clientId?: string;
    projectId?: string;
    creator?: {
        createdBy?: string;
        createdByName?: string;
        createdByEmail?: string;
    };
    quoteNumber?: string;
    title?: string;
    issueDate?: string;
    expiryDate?: string;
    currency?: string;
    footerMessage?: string;
    bankDetails?: string;
    lineItems: QuoteLineItem[];
    deposit?: {
        enabled: boolean;
        mode: 'fixed' | 'percentage';
        value: number;
    };
    paymentPlan?: QuotePaymentPlan;
};

function todayIsoDate(): string {
    return new Date().toISOString().slice(0, 10);
}

function buildQuotePortalPath(clientSlug: string, quoteId: string, tenantId?: string | null): string {
    return buildClientPortalPath({
        clientSlug,
        tenantId: String(tenantId || '').trim(),
        section: 'quotes',
        focusParam: 'focusQuoteId',
        focusValue: quoteId,
    });
}

export const useQuotesStore = defineStore('quotes', {
    state: () => ({
        quotes: [] as Quote[],
        loading: false,
        saving: false,
        error: null as string | null,
        loadedForTenantId: null as string | null,
    }),

    actions: {
        _currentCreatorFields(override?: Pick<Quote, 'createdBy' | 'createdByName' | 'createdByEmail'>) {
            if (override?.createdBy || override?.createdByName || override?.createdByEmail) {
                return buildDocumentCreatorFields({
                    userId: override.createdBy,
                    userName: override.createdByName,
                    userEmail: override.createdByEmail,
                });
            }

            const authStore = useAuthStore();
            const userStore = useUserStore();
            return buildDocumentCreatorFields({
                userId: authStore.user?.uid || userStore.user?.id,
                userName: authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName,
                userEmail: authStore.user?.email || userStore.user?.email,
            });
        },

        _getTenantId(): string {
            const tenantStore = useTenantStore();
            if (!tenantStore.currentTenantId) {
                throw new Error('No tenant selected. Please log in.');
            }
            return tenantStore.currentTenantId;
        },

        _sort(items: Quote[]): Quote[] {
            return [...items].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        },

        async _resolveDocumentRecipient(input: Pick<CreateQuoteInput, 'clientId'>) {
            return await resolveDocumentRecipient({
                clientId: input.clientId,
            });
        },

        async _resolveCanonicalQuoteOwnership(input: Pick<CreateQuoteInput, 'clientId'>) {
            const resolved = await this._resolveDocumentRecipient(input);
            if (!resolved.clientId) {
                throw new Error('Quotes must belong to a client. Create or link the client before saving.');
            }
            return {
                clientId: resolved.clientId,
                resolved,
            };
        },

        _workspaceOrigin(): string {
            const config = useRuntimeConfig();
            return resolveWorkspaceOrigin({
                appDomain: config.public.appDomain,
                fallbackOrigin: window.location.origin,
            });
        },

        buildQuotePublicLink(quote: Pick<Quote, 'id' | 'publicAccessToken'>): string | null {
            const token = String(quote.publicAccessToken || '').trim();
            if (!token) {
                return null;
            }

            return buildPublicQuoteLink({
                origin: this._workspaceOrigin(),
                token,
                tenantId: this._getTenantId(),
                quoteId: quote.id,
            });
        },

        async ensureQuotePublicLink(quoteId: string, quote?: Quote): Promise<string> {
            const token = await this._ensurePublicAccessToken(quoteId, quote);
            return buildPublicQuoteLink({
                origin: this._workspaceOrigin(),
                token,
                tenantId: this._getTenantId(),
                quoteId,
            });
        },

        async _ensurePublicAccessToken(quoteId: string, quote?: Quote): Promise<string> {
            const existing = quote || this.quotes.find(item => item.id === quoteId);
            if (!existing) {
                throw new Error('Quote not found.');
            }

            const currentToken = String(existing.publicAccessToken || '').trim();
            if (currentToken) {
                return currentToken;
            }

            const token = generatePublicAccessToken();
            await this.updateQuote(quoteId, {
                publicAccessToken: token,
                publicSharedAt: new Date(),
                updatedAt: new Date(),
            });

            return token;
        },

        _requireDocumentLinkage(input: Pick<CreateQuoteInput, 'clientId'>) {
            const clientId = String(input.clientId || '').trim() || undefined;
            if (!clientId) {
                throw new Error('Quotes must belong to a client. Choose or create the client first.');
            }

            return { clientId };
        },

        async _sendQuoteEmail(quote: Quote, options?: { subject?: string; actionLink?: string; actionLabel?: string }): Promise<boolean> {
            const resolved = await this._resolveDocumentRecipient(quote);
            if (!resolved.recipientEmail) {
                return false;
            }

            const tenantStore = useTenantStore();
            const actionLink = String(options?.actionLink || '').trim();
            if (!actionLink) {
                return false;
            }

            await $fetch('/api/send-email', {
                method: 'POST',
                headers: await getRequiredAuthHeaders(),
                body: {
                    tenantId: tenantStore.currentTenantId,
                    to: resolved.recipientEmail,
                    template: 'quote_ready',
                    workspaceName: tenantStore.currentTenant?.name || 'Vuey',
                    clientName: resolved.recipientName,
                    recipientName: resolved.recipientName,
                    subject: options?.subject,
                    actionLink,
                    actionLabel: options?.actionLabel || 'Review quote',
                    documentTitle: quote.title,
                    documentNumber: quote.quoteNumber,
                    amountTotal: Number(quote.amountTotal || 0),
                    currency: quote.currency || DEFAULT_CURRENCY,
                    expiryDate: quote.expiryDate,
                },
            });

            return true;
        },

        _buildQuotePayload(input: CreateQuoteInput, options?: {
            status?: Quote['status'];
            clientFacing?: boolean;
            acceptanceSource?: Quote['acceptanceSource'];
            acceptedAt?: unknown;
            rejectedAt?: unknown;
            creator?: Pick<Quote, 'createdBy' | 'createdByName' | 'createdByEmail'>;
        }): Omit<Quote, 'id'> {
            const tenantStore = useTenantStore();
            const entitlements = resolveWorkspaceEntitlements(tenantStore.currentTenant);
            if ((input.deposit?.enabled || input.paymentPlan?.enabled) && !entitlements.plan.features.depositsAndPaymentPlans) {
                throw new Error('Deposits and payment plans are available on the Growth plan and above.');
            }
            const {
                normalizedItems,
                amountTotal,
                deposit,
                paymentPlan,
            } = buildDocumentAccounting<QuoteLineItem, NonNullable<Quote['deposit']>, QuotePaymentPlan>({
                lineItems: input.lineItems,
                deposit: input.deposit,
                paymentPlan: input.paymentPlan,
            });
            const currency = String(input.currency || tenantStore.currentTenant?.paymentSettings?.defaultCurrency || DEFAULT_CURRENCY).trim() || DEFAULT_CURRENCY;

            return stripUndefined({
                tenantId: this._getTenantId(),
                createdBy: options?.creator?.createdBy,
                createdByName: options?.creator?.createdByName,
                createdByEmail: options?.creator?.createdByEmail,
                clientId: input.clientId || undefined,
                projectId: input.projectId || undefined,
                clientFacing: options?.clientFacing ?? false,
                quoteNumber: input.quoteNumber || undefined,
                title: input.title || undefined,
                issueDate: input.issueDate || undefined,
                expiryDate: input.expiryDate || undefined,
                currency,
                status: options?.status || 'draft',
                acceptanceSource: options?.acceptanceSource,
                amountTotal,
                lineItems: normalizedItems,
                deposit,
                paymentPlan,
                footerMessage: input.footerMessage || tenantStore.currentTenant?.paymentSettings?.invoiceFooterMessage || undefined,
                bankDetails: input.bankDetails || tenantStore.currentTenant?.paymentSettings?.bankDetails || undefined,
                acceptedAt: options?.acceptedAt,
                rejectedAt: options?.rejectedAt,
                updatedAt: new Date(),
            }) as Omit<Quote, 'id'>;
        },

        async fetchAll(options: FreshRequestOptions = {}): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const tenantStore = useTenantStore();
            this.error = null;

            try {
                await runFreshRequest(`quotes:${tenantId}:all`, async () => {
                    this.loading = true;
                    this.quotes = this._sort(await fetchTenantCollection<Quote>($db, tenantId, 'quotes'));
                    if (this.quotes.some(quote => !String(quote.quoteNumber || '').trim())
                        && ['owner', 'admin', 'member'].includes(String(tenantStore.currentTenantRole || ''))) {
                        try {
                            const assigned = await reconcileMissingCommercialDocumentNumbers('quote');
                            const assignedById = new Map(assigned.map(item => [item.id, item.number]));
                            this.quotes = this._sort(this.quotes.map(quote => ({
                                ...quote,
                                ...(assignedById.has(quote.id) ? { quoteNumber: assignedById.get(quote.id) } : {}),
                            })));
                        } catch (reconcileError) {
                            console.warn('Missing quote numbers could not be reconciled:', reconcileError);
                        }
                    }
                    this.loadedForTenantId = tenantId;
                }, {
                    ...options,
                    hasData: this.loadedForTenantId === tenantId,
                });
            } catch (error) {
                this.error = `Error loading quotes: ${error}`;
                console.error(this.error);
            } finally {
                this.loading = false;
            }
        },

        async fetchById(quoteId: string): Promise<Quote | null> {
            const cached = this.quotes.find(item => item.id === quoteId);
            if (cached) return cached;
            const { $db } = useNuxtApp();
            if (!$db) throw new Error('Firestore is not available.');
            const snapshot = await getDoc(doc($db, `tenants/${this._getTenantId()}/quotes`, quoteId));
            if (!snapshot.exists()) return null;
            const quote = { ...(snapshot.data() as Quote), id: snapshot.id };
            this.quotes = this._sort([quote, ...this.quotes]);
            return quote;
        },

        async fetchForClient(clientId: string): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.loading = true;
            this.error = null;
            this.loadedForTenantId = null;

            try {
                this.quotes = this._sort(
                    await fetchTenantCollection<Quote>($db, tenantId, 'quotes', [where('clientId', '==', clientId)]),
                );
            } catch (error) {
                this.error = `Error loading quotes: ${error}`;
                console.error(this.error);
                this.quotes = [];
            } finally {
                this.loading = false;
            }
        },

        async fetchForProject(projectId: string): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.loading = true;
            this.error = null;
            this.loadedForTenantId = null;

            try {
                this.quotes = this._sort(
                    await fetchTenantCollection<Quote>($db, tenantId, 'quotes', [where('projectId', '==', projectId)]),
                );
            } catch (error) {
                this.error = `Error loading quotes: ${error}`;
                console.error(this.error);
                this.quotes = [];
            } finally {
                this.loading = false;
            }
        },

        async createQuote(input: CreateQuoteInput): Promise<string> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.saving = true;
            this.error = null;
            let createdQuoteId = '';

            try {
                const linkage = this._requireDocumentLinkage(input);
                const resolvedLinkage = await this._resolveCanonicalQuoteOwnership(linkage);
                const quoteNumber = String(input.quoteNumber || '').trim()
                    || await requestNextCommercialDocumentNumber('quote', resolvedLinkage.clientId);
                const payload = {
                    ...this._buildQuotePayload({
                        ...input,
                        quoteNumber: undefined,
                        clientId: resolvedLinkage.clientId,
                    }, {
                        creator: this._currentCreatorFields(input.creator),
                    }),
                    tenantId,
                    createdAt: new Date(),
                };

                const quoteId = await createTenantDocument($db, tenantId, 'quotes', payload as TenantDocumentPayload);
                createdQuoteId = quoteId;
                await assignCommercialDocumentNumber('quote', quoteId, quoteNumber);
                this.quotes = this._sort([{ ...(payload as Quote), id: quoteId, quoteNumber }, ...this.quotes]);

                return quoteId;
            } catch (error: any) {
                if (createdQuoteId) {
                    await $fetch('/api/commercial-documents/delete', {
                        method: 'POST', headers: await getRequiredAuthHeaders(),
                        body: { tenantId, kind: 'quote', id: createdQuoteId },
                    }).catch(() => undefined);
                }
                this.error = error?.message || 'Failed to create quote.';
                console.error('Error creating quote:', error);
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async saveQuote(quoteId: string, input: CreateQuoteInput): Promise<void> {
            const existing = this.quotes.find(item => item.id === quoteId);
            if (!existing) throw new Error('Quote not found.');
            const linkage = this._requireDocumentLinkage({
                clientId: input.clientId ?? existing.clientId,
            });
            const resolvedLinkage = await this._resolveCanonicalQuoteOwnership(linkage);
            const acceptanceSource = resolveQuoteAcceptanceSource(existing);

            const payload = this._buildQuotePayload({
                ...input,
                clientId: resolvedLinkage.clientId,
            }, {
                status: existing.status === 'accepted' ? 'accepted' : 'draft',
                clientFacing: acceptanceSource === 'client' ? true : existing.clientFacing,
                acceptanceSource: acceptanceSource || undefined,
                acceptedAt: existing.status === 'accepted' ? existing.acceptedAt : undefined,
                rejectedAt: undefined,
                creator: existing.status === 'draft'
                    ? this._currentCreatorFields(input.creator || {
                        createdBy: existing.createdBy,
                        createdByName: existing.createdByName,
                        createdByEmail: existing.createdByEmail,
                    })
                    : {
                        createdBy: existing.createdBy,
                        createdByName: existing.createdByName,
                        createdByEmail: existing.createdByEmail,
                    },
            });

            await this.updateQuote(quoteId, payload);
        },

        async updateQuote(quoteId: string, updates: Partial<Quote>): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.error = null;

            try {
                const requestedNumber = Object.prototype.hasOwnProperty.call(updates, 'quoteNumber')
                    ? String(updates.quoteNumber || '').trim()
                    : '';
                const { quoteNumber: _ignoredQuoteNumber, ...safeUpdates } = updates as Partial<Quote>;
                const assignedNumber = requestedNumber
                    ? await assignCommercialDocumentNumber('quote', quoteId, requestedNumber)
                    : undefined;
                const patch = stripUndefined({ ...safeUpdates, updatedAt: new Date() });
                await updateTenantDocument($db, tenantId, 'quotes', quoteId, patch as TenantDocumentPayload);

                const idx = this.quotes.findIndex(item => item.id === quoteId);
                if (idx >= 0) {
                    const existing = this.quotes[idx] as Quote;
                    this.quotes[idx] = { ...existing, ...patch, ...(assignedNumber ? { quoteNumber: assignedNumber } : {}) } as Quote;
                    this.quotes = this._sort([...this.quotes]);
                }
            } catch (error: any) {
                this.error = error?.message || 'Failed to update quote.';
                console.error('Error updating quote:', error);
                throw error;
            }
        },

        async sendQuote(quoteId: string): Promise<void> {
            const existing = this.quotes.find(item => item.id === quoteId);
            if (!existing) {
                throw new Error('Quote not found.');
            }
            const resolved = await this._resolveDocumentRecipient(existing);
            if (!resolved.recipientEmail) {
                throw new Error('This client needs an email address before the quote can be sent.');
            }
            const tenantStore = useTenantStore();
            const config = useRuntimeConfig();
            const portalOrigin = resolveClientEmailOrigin({
                tenant: tenantStore.currentTenant,
                appDomain: config.public.appDomain,
                fallbackOrigin: window.location.origin,
            });
            const clientSlug = String(resolved.client?.slug || '').trim();
            let publicAccessToken = String(existing.publicAccessToken || '').trim();
            const portalQuoteLink = clientSlug
                ? buildClientPortalLink({
                    origin: portalOrigin,
                    tenantId: this._getTenantId(),
                    clientSlug,
                    section: 'quotes',
                    focusParam: 'focusQuoteId',
                    focusValue: quoteId,
                })
                : '';

            if (!portalQuoteLink) {
                publicAccessToken = await this._ensurePublicAccessToken(quoteId, existing);
            }

            const publicQuoteLink = publicAccessToken
                ? buildPublicQuoteLink({
                    origin: this._workspaceOrigin(),
                    token: publicAccessToken,
                    tenantId: this._getTenantId(),
                    quoteId,
                })
                : '';

            await this.updateQuote(quoteId, {
                clientId: existing.clientId || resolved.clientId || undefined,
                status: 'sent',
                clientFacing: true,
                ...(publicAccessToken ? { publicAccessToken, publicSharedAt: new Date() } : {}),
                updatedAt: new Date(),
            });

            const sentQuote = this.quotes.find(item => item.id === quoteId) || {
                ...existing,
                clientId: existing.clientId || resolved.clientId || undefined,
                status: 'sent',
                clientFacing: true,
                ...(publicAccessToken ? { publicAccessToken } : {}),
            };
            try {
                await this._sendQuoteEmail(sentQuote as Quote, {
                    actionLink: portalQuoteLink || publicQuoteLink,
                    actionLabel: portalQuoteLink ? 'Review quote' : 'Open quote',
                });
            } catch (error) {
                console.warn('Quote email could not be sent:', error);
            }

            try {
                if (resolved.clientId && clientSlug) {
                    const portalQuotePath = buildQuotePortalPath(clientSlug, quoteId, this._getTenantId());
                    await dispatchNotification({
                        tenantId: this._getTenantId(),
                        surface: 'client',
                        eventType: 'quote_ready',
                        title: existing.title ? `Quote ready: ${existing.title}` : 'A quote is ready to review',
                        body: 'Your team has shared a quote in the portal.',
                        recipients: [{
                            type: 'portal_access',
                            clientId: resolved.clientId,
                            projectId: existing.projectId,
                            permission: 'viewQuotes',
                        }],
                        entityType: 'quote',
                        entityId: quoteId,
                        clientId: resolved.clientId,
                        projectId: existing.projectId,
                        actionPath: portalQuotePath,
                        actionLabel: 'Review quote',
                        email: {
                            template: 'quote_ready',
                            actionLink: portalQuoteLink,
                            actionLabel: 'Review quote',
                            clientName: resolved.recipientName,
                            documentTitle: existing.title,
                            documentNumber: existing.quoteNumber,
                            amountTotal: Number(existing.amountTotal || 0),
                            currency: existing.currency || DEFAULT_CURRENCY,
                            expiryDate: existing.expiryDate,
                        },
                    });
                }
            } catch (notificationError) {
                console.warn('Quote notification could not be created:', notificationError);
            }
        },

        async sendQuoteReminder(quoteId: string): Promise<void> {
            const existing = this.quotes.find(item => item.id === quoteId);
            if (!existing) {
                throw new Error('Quote not found.');
            }
            if (existing.status !== 'sent' || existing.clientFacing === false) {
                throw new Error('Only sent client-facing quotes can be reminded.');
            }
            const resolved = await this._resolveDocumentRecipient(existing);
            if (!resolved.recipientEmail) {
                throw new Error('This client needs an email address before the quote reminder can be sent.');
            }

            const tenantStore = useTenantStore();
            const config = useRuntimeConfig();
            const quoteLabel = existing.title || existing.quoteNumber || 'your quote';
            const portalOrigin = resolveClientEmailOrigin({
                tenant: tenantStore.currentTenant,
                appDomain: config.public.appDomain,
                fallbackOrigin: window.location.origin,
            });
            const clientSlug = String(resolved.client?.slug || '').trim();
            let publicAccessToken = String(existing.publicAccessToken || '').trim();
            const portalQuoteLink = clientSlug
                ? buildClientPortalLink({
                    origin: portalOrigin,
                    tenantId: this._getTenantId(),
                    clientSlug,
                    section: 'quotes',
                    focusParam: 'focusQuoteId',
                    focusValue: quoteId,
                })
                : '';
            if (!portalQuoteLink) {
                publicAccessToken = await this._ensurePublicAccessToken(quoteId, existing);
            }
            const publicQuoteLink = publicAccessToken
                ? buildPublicQuoteLink({
                    origin: this._workspaceOrigin(),
                    token: publicAccessToken,
                    tenantId: this._getTenantId(),
                    quoteId,
                })
                : '';
            const emailSent = await this._sendQuoteEmail(existing, {
                subject: `Reminder: ${tenantStore.currentTenant?.name || 'Vuey'} shared ${quoteLabel}`,
                actionLink: portalQuoteLink || publicQuoteLink,
                actionLabel: portalQuoteLink ? 'Review quote' : 'Open quote',
            });

            if (!emailSent) {
                throw new Error('This client needs a reachable email before a reminder can be sent.');
            }

            try {
                const authStore = useAuthStore();
                const userStore = useUserStore();
                if (resolved.clientId && clientSlug) {
                    const portalQuotePath = buildQuotePortalPath(clientSlug, quoteId, this._getTenantId());
                    await dispatchNotification({
                        tenantId: this._getTenantId(),
                        surface: 'client',
                        eventType: 'quote_ready',
                        title: `Reminder: ${quoteLabel} is waiting for review`,
                        body: 'Your team has sent a reminder for this quote.',
                        recipients: [{
                            type: 'portal_access',
                            clientId: resolved.clientId,
                            projectId: existing.projectId,
                            permission: 'viewQuotes',
                        }],
                        entityType: 'quote',
                        entityId: quoteId,
                        clientId: resolved.clientId,
                        projectId: existing.projectId,
                        actionPath: portalQuotePath,
                        actionLabel: 'Review quote',
                        actorId: authStore.user?.uid || userStore.user?.id,
                        actorName: authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName,
                        actorAvatarURL: userStore.user?.avatarURL,
                        email: {
                            template: 'quote_ready',
                            actionLink: portalQuoteLink,
                            actionLabel: 'Review quote',
                            clientName: resolved.recipientName,
                            documentTitle: existing.title,
                            documentNumber: existing.quoteNumber,
                            amountTotal: Number(existing.amountTotal || 0),
                            currency: existing.currency || DEFAULT_CURRENCY,
                            expiryDate: existing.expiryDate,
                        },
                    });
                }
            } catch (notificationError) {
                console.warn('Quote reminder notification could not be created:', notificationError);
            }
        },

        async acceptQuote(quoteId: string): Promise<void> {
            await this.updateQuote(quoteId, {
                status: 'accepted',
                clientFacing: true,
                acceptanceSource: 'client',
                acceptedAt: new Date(),
                updatedAt: new Date(),
            });
        },

        async approveQuoteInternally(quoteId: string): Promise<void> {
            await this.updateQuote(quoteId, {
                status: 'accepted',
                clientFacing: false,
                acceptanceSource: 'internal',
                acceptedAt: new Date(),
                updatedAt: new Date(),
            });
        },

        async rejectQuote(quoteId: string): Promise<void> {
            await this.updateQuote(quoteId, {
                status: 'rejected',
                rejectedAt: new Date(),
                updatedAt: new Date(),
            });
        },

        async _getQuoteInvoices(quoteId: string) {
            const invoicesStore = useInvoicesStore();
            if (!invoicesStore.invoices.length) {
                await invoicesStore.fetchAll();
            }
            return invoicesStore.invoices.filter(invoice => invoice.quoteId === quoteId);
        },

        _requireAcceptedQuote(quoteId: string): Quote {
            const quote = this.quotes.find(item => item.id === quoteId);
            if (!quote) throw new Error('Quote not found.');
            if (quote.status !== 'accepted') {
                throw new Error('Only accepted quotes can generate invoices.');
            }
            return quote;
        },

        async createFullInvoiceFromQuote(quoteId: string): Promise<string> {
            const quote = this._requireAcceptedQuote(quoteId);
            const existing = await this._getQuoteInvoices(quoteId);
            if (existing.some(item => item.quoteSource === 'full')) {
                throw new Error('A full invoice has already been generated from this quote.');
            }

            const invoicesStore = useInvoicesStore();
            const invoiceId = await invoicesStore.createInvoice({
                clientId: quote.clientId,
                projectId: quote.projectId,
                quoteId: quote.id,
                quoteSource: 'full',
                title: quote.title ? `${quote.title} (Full invoice)` : 'Full invoice',
                issueDate: todayIsoDate(),
                dueDate: quote.expiryDate || todayIsoDate(),
                currency: quote.currency || 'USD',
                footerMessage: quote.footerMessage,
                bankDetails: quote.bankDetails,
                lineItems: quote.lineItems || [{
                    name: quote.title || 'Quote total',
                    description: '',
                    quantity: 1,
                    price: Number(quote.amountTotal || 0),
                    discount: 0,
                }],
            });

            await this.updateQuote(quoteId, {
                generatedInvoiceIds: Array.from(new Set([...(quote.generatedInvoiceIds || []), invoiceId])),
            });

            return invoiceId;
        },

        async createDepositInvoiceFromQuote(quoteId: string): Promise<string> {
            const quote = this._requireAcceptedQuote(quoteId);
            const deposit = Number(quote.deposit?.amount || 0);
            if (!quote.deposit?.enabled || deposit <= 0) {
                throw new Error('This quote has no deposit configured.');
            }

            const existing = await this._getQuoteInvoices(quoteId);
            if (existing.some(item => item.quoteSource === 'deposit')) {
                throw new Error('A deposit invoice has already been generated from this quote.');
            }

            const invoicesStore = useInvoicesStore();
            const invoiceId = await invoicesStore.createInvoice({
                clientId: quote.clientId,
                projectId: quote.projectId,
                quoteId: quote.id,
                quoteSource: 'deposit',
                title: quote.title ? `${quote.title} (Deposit)` : 'Deposit invoice',
                issueDate: todayIsoDate(),
                dueDate: quote.issueDate || todayIsoDate(),
                currency: quote.currency || 'USD',
                footerMessage: quote.footerMessage,
                bankDetails: quote.bankDetails,
                lineItems: [{
                    name: 'Deposit',
                    description: quote.title || 'Quote deposit',
                    quantity: 1,
                    price: deposit,
                    discount: 0,
                }],
            });

            await this.updateQuote(quoteId, {
                generatedInvoiceIds: Array.from(new Set([...(quote.generatedInvoiceIds || []), invoiceId])),
            });

            return invoiceId;
        },

        async createBalanceInvoiceFromQuote(quoteId: string): Promise<string> {
            const quote = this._requireAcceptedQuote(quoteId);
            const deposit = quote.deposit?.enabled ? Number(quote.deposit.amount || 0) : 0;
            const balance = Number(Math.max(0, Number(quote.amountTotal || 0) - deposit).toFixed(2));
            if (balance <= 0) {
                throw new Error('There is no remaining balance for this quote.');
            }

            const existing = await this._getQuoteInvoices(quoteId);
            if (existing.some(item => item.quoteSource === 'balance')) {
                throw new Error('A balance invoice has already been generated from this quote.');
            }

            const invoicesStore = useInvoicesStore();
            const invoiceId = await invoicesStore.createInvoice({
                clientId: quote.clientId,
                projectId: quote.projectId,
                quoteId: quote.id,
                quoteSource: 'balance',
                title: quote.title ? `${quote.title} (Balance)` : 'Balance invoice',
                issueDate: todayIsoDate(),
                dueDate: quote.expiryDate || todayIsoDate(),
                currency: quote.currency || 'USD',
                footerMessage: quote.footerMessage,
                bankDetails: quote.bankDetails,
                lineItems: [{
                    name: 'Remaining balance',
                    description: quote.title || 'Remaining quote balance',
                    quantity: 1,
                    price: balance,
                    discount: 0,
                }],
            });

            await this.updateQuote(quoteId, {
                generatedInvoiceIds: Array.from(new Set([...(quote.generatedInvoiceIds || []), invoiceId])),
            });

            return invoiceId;
        },

        async createNextInstallmentInvoiceFromQuote(quoteId: string, options?: { dueOnly?: boolean }): Promise<string> {
            const quote = this._requireAcceptedQuote(quoteId);
            const installments = quote.paymentPlan?.installments || [];
            if (!quote.paymentPlan?.enabled || !installments.length) {
                throw new Error('This quote has no payment plan configured.');
            }

            const existing = await this._getQuoteInvoices(quoteId);
            const generatedIdx = new Set(
                existing
                    .filter(item => item.quoteSource === 'installment' && typeof item.quoteInstallmentIndex === 'number')
                    .map(item => Number(item.quoteInstallmentIndex)),
            );

            const today = todayIsoDate();
            let idx = installments.findIndex((item, index) => !generatedIdx.has(index) && (!item.dueDate || item.dueDate <= today));
            if (idx === -1 && !options?.dueOnly) {
                idx = installments.findIndex((_, index) => !generatedIdx.has(index));
            }

            if (idx === -1) {
                throw new Error(options?.dueOnly
                    ? 'No due installments are ready for invoicing.'
                    : 'All installment invoices have already been generated.');
            }

            const installment = installments[idx];
            if (!installment) {
                throw new Error('Unable to resolve the next installment.');
            }
            const amount = amountFromInstallment(Number(quote.amountTotal || 0), installment);
            if (amount <= 0) {
                throw new Error('The next installment amount is zero.');
            }

            const invoicesStore = useInvoicesStore();
            const invoiceId = await invoicesStore.createInvoice({
                clientId: quote.clientId,
                projectId: quote.projectId,
                quoteId: quote.id,
                quoteSource: 'installment',
                quoteInstallmentIndex: idx,
                title: quote.title ? `${quote.title} (${installmentLabel(quote.paymentPlan?.installments, idx)})` : installmentLabel(quote.paymentPlan?.installments, idx),
                issueDate: todayIsoDate(),
                dueDate: installment.dueDate || todayIsoDate(),
                currency: quote.currency || 'USD',
                footerMessage: quote.footerMessage,
                bankDetails: quote.bankDetails,
                lineItems: [{
                    name: installmentLabel(quote.paymentPlan?.installments, idx),
                    description: quote.title || 'Quote installment',
                    quantity: 1,
                    price: amount,
                    discount: 0,
                }],
            });

            const nextInstallments = installments.map((item, index) => {
                if (index !== idx) return item;
                return {
                    ...item,
                    status: 'invoiced' as const,
                };
            });

            await this.updateQuote(quoteId, {
                paymentPlan: {
                    ...(quote.paymentPlan || { enabled: true, installments: [] }),
                    enabled: true,
                    installments: nextInstallments,
                },
                generatedInvoiceIds: Array.from(new Set([...(quote.generatedInvoiceIds || []), invoiceId])),
            });

            return invoiceId;
        },

        async autoCreateDueInstallmentsForAcceptedQuotes(): Promise<void> {
            const candidates = this.quotes.filter((quote) => (
                quote.status === 'accepted'
                && quote.paymentPlan?.enabled
                && (quote.paymentPlan.installments?.length || 0) > 0
            ));
            const invoicesStore = useInvoicesStore();

            for (const quote of candidates) {
                while (true) {
                    try {
                        const invoiceId = await this.createNextInstallmentInvoiceFromQuote(quote.id, { dueOnly: true });
                        try {
                            await invoicesStore.sendInvoice(invoiceId);
                        } catch (sendError) {
                            console.warn('Auto-generated installment invoice was created but could not be sent automatically:', sendError);
                        }
                    } catch {
                        break;
                    }
                }
            }
        },

        async deleteQuote(quoteId: string): Promise<void> {
            const { $auth } = useNuxtApp();
            const tenantId = this._getTenantId();
            const existing = this.quotes.find(item => item.id === quoteId);
            if (!existing) {
                throw new Error('Quote not found.');
            }

            const invoicesStore = useInvoicesStore();
            const linkedInvoices = invoicesStore.invoices.filter(invoice => invoice.quoteId === quoteId);
            if (linkedInvoices.length > 0) {
                throw new Error('Delete linked invoices first before deleting this quote.');
            }

            this.error = null;

            try {
                if (!$auth.currentUser) throw new Error('Please sign in before deleting a quote.');
                await $fetch('/api/commercial-documents/delete', {
                    method: 'POST',
                    headers: { authorization: `Bearer ${await $auth.currentUser.getIdToken()}` },
                    body: { tenantId, kind: 'quote', id: quoteId },
                });
                this.quotes = this.quotes.filter(item => item.id !== quoteId);
            } catch (error: any) {
                this.error = error?.message || 'Failed to delete quote.';
                console.error('Error deleting quote:', error);
                throw error;
            }
        },
    },

    getters: {
        quotesByClient: (state) => (clientId: string) => {
            const normalizedClientId = String(clientId || '').trim();
            if (!normalizedClientId) {
                return [];
            }

            return state.quotes.filter((item) => item.clientId === normalizedClientId);
        },
        quotesByProject: (state) => (projectId: string) => state.quotes.filter(item => item.projectId === projectId),
    },
});
